import type {HighlightedEvent} from '@github-ui/timeline-items/HighlightedEvent'
import type {IssueEventRationales$data} from '@github-ui/timeline-items/IssueEventRationales'
import {wrapElement} from '@github-ui/timeline-items/LayoutHelpers'
import {useSecondaryTimelineQuery} from '@github-ui/timeline-items/SecondaryTimelineQuery'
import {VALUES} from '@github-ui/timeline-items/Values'
// Using getFocusableChild for a callback after client side loading, won't affect SSR
// eslint-disable-next-line no-restricted-imports
import {getFocusableChild} from '@primer/behaviors/utils'
import {type ReactNode, type RefObject, useEffect} from 'react'

import type {RolledUpTimelineItem} from '../../utils/timeline-rollups'
import type {IssueTimelineItem$data} from './__generated__/IssueTimelineItem.graphql'
import {FailedLoadTimelineItem} from './FailedLoadTimelineItem'
import type {RenderItem, TimelineItem} from './IssueTimeline'
import styles from './IssueTimeline.module.css'
import {type EventProps, IssueTimelineItem} from './IssueTimelineItem'
import {LoadMore} from './LoadMore'
import {isEventHighlighted} from './use-timeline-highlight'

type VirtualIssueTimelinePageProps = {
  timelineItems: TimelineItem[]
  shouldHighlightElement: boolean
  highlight?: HighlightedEvent
  highlightedItemRef: RefObject<HTMLDivElement | null>
  issueId: string
  repositoryId: string
  repositoryNameWithOwner: string
  issueUrl: string
  showCopilotMenuItem?: boolean
  eventRationales?: IssueEventRationales$data
} & EventProps

function shouldAddDivider(items: TimelineItem[], index: number) {
  if (index === 0) {
    return true
  }
  if (items[index - 1]?.type === 'load') {
    return false
  }
  const item = items[index]
  const previousItem = items[index - 1]
  if (!item || !previousItem) {
    return false
  }

  if (item.type === 'event' && previousItem.type === 'event') {
    const eventType = item.item?.__typename
    const previousEventType = previousItem.item?.__typename
    if (eventType && previousEventType) {
      const isMajorEvent = VALUES.timeline.majorEventTypes.includes(eventType)
      const previousIsMajorEvent = VALUES.timeline.majorEventTypes.includes(previousEventType)

      if (isMajorEvent === previousIsMajorEvent) {
        if (eventType !== 'IssueComment' && previousEventType !== 'IssueComment') {
          return false
        }
      }
    }
  }

  return true
}

export function VirtualIssueTimelinePage({
  timelineItems,
  shouldHighlightElement,
  highlight,
  viewer,
  issueId,
  repositoryId,
  repositoryNameWithOwner,
  issueUrl,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
  highlightedItemRef,
  showCopilotMenuItem = false,
  eventRationales,
}: VirtualIssueTimelinePageProps): React.ReactElement {
  const focusFirstLoadedItem = (items: TimelineItem[], currentIndex: number) => {
    // A timeout is needed to trigger the focusing asynchronously
    // so it doesn't happen before Relay loads the new timeline items
    setTimeout(() => {
      // We want to use the previous item as a base, since the load button
      // will get pushed down and/or unmounted, when all items are loaded.
      const baseItem = items[currentIndex - 1]
      if (!baseItem || baseItem.type !== 'event' || !baseItem.item?.__id) return

      // To avoid complex propagation of refs, we instead query via data attributes set
      // in the TimelineRowBorder component and take the next element, which should be
      // the first of the newly loaded events.
      const nextItemSelector = `[${VALUES.timeline.dataWrapperTimelineEventId}="${baseItem.item.__id}"] + *`
      const nextEvent = document.querySelector<HTMLElement>(nextItemSelector)

      let focusTarget: HTMLElement | null | undefined = null

      if (nextEvent) {
        focusTarget = getFocusableChild(nextEvent)
      } else {
        // Handle case where base item is inside a <section> and doesn't have a next sibling (All events that are not comments are grouped inside <section> tags.)
        const baseItemSelector = `[${VALUES.timeline.dataWrapperTimelineEventId}="${baseItem.item.__id}"]`
        const baseElement = document.querySelector<HTMLElement>(baseItemSelector)
        const section = baseElement?.closest('section')
        const isGroupedEvent = baseItem.item.__typename !== 'IssueComment'

        if (section && isGroupedEvent) {
          const nextSibling = section.nextElementSibling as HTMLElement | null
          if (nextSibling) {
            focusTarget = getFocusableChild(nextSibling)
          }
        }
      }

      focusTarget?.focus({preventScroll: true})
    })
  }

  const {secondaryTimelineQueryRef, loadSecondaryTimelineQueryRef, dispose} = useSecondaryTimelineQuery()

  const hasLazyData = secondaryTimelineQueryRef !== null
  useEffect(() => {
    const nodesIds = timelineItems
      .filter(item => item.type === 'event' && item.item?.__typename === 'ReferencedEvent')
      .map(timelineNode => {
        const node = timelineNode as RolledUpTimelineItem<IssueTimelineItem$data>
        if (node.rollupGroup && node.rollupGroup['ReferencedEvent']) {
          return node.rollupGroup['ReferencedEvent'].map(rollup => rollup.__id)
        }

        return node && node.item ? [node.item.__id] : []
      })
      .flat()
      .filter((id): id is string => id !== null)
    if (nodesIds.length > 0) {
      loadSecondaryTimelineQueryRef({nodes: nodesIds})
    }
    return () => {
      dispose()
    }
  }, [timelineItems, hasLazyData, dispose, loadSecondaryTimelineQueryRef])

  const rationaleByEventId = new Map(
    eventRationales?.eventRationales.filter(r => r.issueEvent?.id).map(r => [r.issueEvent!.id, r] as const) ?? [],
  )

  const reactElements = timelineItems
    .map((timelineItem: TimelineItem, index, initialTimelineItems): RenderItem => {
      if (timelineItem.type === 'load') {
        const fullType: 'load-top' | 'load-bottom' = `${timelineItem.type}-${timelineItem.position}`

        const wrappedElement = (
          <div className={styles.loadMoreWrapper}>
            <LoadMore
              key={fullType}
              type={fullType}
              loadFromTopFn={timelineItem.loadFromTop}
              loadFromBottomFn={timelineItem.loadFromBottom}
              numberOfRemainingItems={timelineItem.numberOfRemainingItems}
              lastItemInTopTimelineIsComment
              firstItemInBottomTimelineIsComment
              onLoadAllComplete={() => focusFirstLoadedItem(initialTimelineItems, index)}
              onLoadFromTopComplete={() => focusFirstLoadedItem(initialTimelineItems, index)}
              onLoadFromBottomComplete={() => focusFirstLoadedItem(initialTimelineItems, index)}
              isCurrentlyLoadingBackItems={timelineItem.isCurrentlyLoading}
            >
              Load more
            </LoadMore>
          </div>
        )

        return {
          isAddedToGroupedEvents: false,
          timelineItem,
          render: wrapElement(wrappedElement, undefined, fullType),
        }
      }

      if (timelineItem.item == null) {
        return {
          isAddedToGroupedEvents: false,
          timelineItem,
          render: <FailedLoadTimelineItem key="failed-load-item" />,
        }
      }

      const addDivider = shouldAddDivider(timelineItems || [], index)
      const isHighlighted = shouldHighlightElement && highlight && isEventHighlighted(timelineItem.item, highlight)
      const ownerLogin = repositoryNameWithOwner.split('/')[0]!

      const eventIds = [
        ...Object.values(timelineItem.rollupGroup ?? {})
          .flatMap(rollups => rollups ?? [])
          .map(r => r.__id),
        ...(timelineItem.item.__id ? [timelineItem.item.__id] : []),
      ]

      const rationales: Record<string, IssueEventRationales$data['eventRationales'][number]> = {}
      for (const eventId of eventIds) {
        const rationale = rationaleByEventId.get(eventId)
        if (rationale) rationales[eventId] = rationale
      }

      return {
        isAddedToGroupedEvents: false,
        timelineItem,
        render: (
          <IssueTimelineItem
            key={timelineItem.item.__id}
            item={timelineItem}
            issueId={issueId}
            repositoryId={repositoryId}
            repositoryNameWithOwner={repositoryNameWithOwner}
            ownerLogin={ownerLogin}
            issueUrl={issueUrl}
            viewer={viewer}
            onCommentChange={onCommentChange}
            onCommentReply={onCommentReply}
            onCommentEditCancel={onCommentEditCancel}
            refAttribute={isHighlighted ? highlightedItemRef : undefined}
            optionConfig={optionConfig}
            isHighlighted={isHighlighted}
            addDivider={addDivider}
            secondaryTimelineQueryRef={hasLazyData ? secondaryTimelineQueryRef : undefined}
            showCopilotMenuItem={showCopilotMenuItem}
            rationales={rationales}
          />
        ),
      }
    })
    .reduce((newArr, currentItem, currentIndex, allItems) => {
      // This is grouping event items that are rendered next to eachother
      // inside <section> tags for adding the correct landmarks for screenreaders
      //
      // All events that are not comments, will be grouped.
      //
      // https://github.com/github/accessibility/issues/5224#issuecomment-1846919306

      if (currentItem.isAddedToGroupedEvents) return newArr
      if (currentItem.timelineItem.type !== 'event' || currentItem.timelineItem.item?.__typename === 'IssueComment') {
        currentItem.isAddedToGroupedEvents = true
        newArr.push(currentItem.render)
        return newArr
      }

      const endSectionIndex = allItems.findIndex((renderItem, index) => {
        return (
          index > currentIndex &&
          (renderItem.timelineItem.type !== 'event' || renderItem.timelineItem.item?.__typename === 'IssueComment')
        )
      })
      const sectionElement = (
        <section key={`events-${currentItem.timelineItem.item?.__id}`} aria-label="Events">
          {allItems.slice(currentIndex, endSectionIndex > -1 ? endSectionIndex : undefined).map(item => {
            item.isAddedToGroupedEvents = true
            return item.render
          })}
        </section>
      )

      newArr.push(sectionElement)
      return newArr
    }, [] as ReactNode[])
  return <>{reactElements}</>
}
